import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EXTRA_PRICING_UNITS from "@/constants/ExtraPricingUnits";
import ApiError from "@/model/ApiError";
import ExtraPricingUnit from "@/model/ExtraPricingUnit";
import ExtraRequest from "@/model/ExtraRequest";
import ExtraResponse from "@/model/ExtraResponse";
import "@/components/ExtraForm/ExtraForm.scss";

interface ExtraFormProps {
  title: string;
  submitLabel: string;
  initialExtra?: ExtraResponse;
  onSubmit: (request: ExtraRequest) => Promise<void>;
}

function ExtraForm({ title, submitLabel, initialExtra, onSubmit }: ExtraFormProps) {
  const { t } = useTranslation();

  const [name, setName] = useState(initialExtra?.name ?? "");
  const [description, setDescription] = useState(initialExtra?.description ?? "");
  const [pricingUnit, setPricingUnit] = useState<ExtraPricingUnit>(
    initialExtra?.pricingUnit ?? "PER_STAY"
  );
  const [priceAmount, setPriceAmount] = useState(
    initialExtra?.priceAmount !== undefined ? String(initialExtra.priceAmount) : ""
  );
  const [priceCurrency, setPriceCurrency] = useState(
    initialExtra?.priceCurrency ?? "EUR"
  );

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        pricingUnit,
        priceAmount: Number(priceAmount),
        priceCurrency: priceCurrency.trim() || undefined,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("extras.form.conflictError"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(t("extras.form.validationError"));
      } else if (err instanceof ApiError && err.status === 404) {
        setError(t("extras.form.notFoundError"));
      } else {
        setError(t("extras.form.genericError"));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <form className="extra-form" onSubmit={handleSubmit}>
      <h1>{title}</h1>

      <label htmlFor="name">{t("extras.form.nameLabel")}</label>
      <input
        id="name"
        type="text"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />

      <label htmlFor="description">{t("extras.form.descriptionLabel")}</label>
      <textarea
        id="description"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />

      <label htmlFor="pricingUnit">{t("extras.form.pricingUnitLabel")}</label>
      <select
        id="pricingUnit"
        value={pricingUnit}
        onChange={(event) => setPricingUnit(event.target.value as ExtraPricingUnit)}
      >
        {EXTRA_PRICING_UNITS.map((unit) => (
          <option key={unit} value={unit}>
            {t(`extras.pricingUnit.${unit}`)}
          </option>
        ))}
      </select>

      <label htmlFor="priceAmount">{t("extras.form.priceAmountLabel")}</label>
      <input
        id="priceAmount"
        type="number"
        min={0.01}
        step="0.01"
        value={priceAmount}
        onChange={(event) => setPriceAmount(event.target.value)}
        required
      />

      <label htmlFor="priceCurrency">{t("extras.form.priceCurrencyLabel")}</label>
      <input
        id="priceCurrency"
        type="text"
        maxLength={3}
        value={priceCurrency}
        onChange={(event) => setPriceCurrency(event.target.value.toUpperCase())}
      />
      <p className="extra-form__help">{t("extras.form.currencyHelp")}</p>

      {error && <p className="extra-form__error">{error}</p>}

      <div className="extra-form__actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("extras.form.submitting") : submitLabel}
        </button>
        <Link to="/extras">{t("extras.form.cancel")}</Link>
      </div>
    </form>
  );
}

export default ExtraForm;
export type { ExtraFormProps };
